/* eslint-disable max-len */
import { DOCUMENT, NgClass } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnInit, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { QUOTE_CONTEXTS, QuoteContextAcquisitionModel, QuoteModel } from '@bytel/bytel-sales';
import {
    AddFundingService,
    CheckoutCartModel,
    EdpService,
    FUNDING_ENUM,
    QuoteContextRenewalModel,
} from '@bytel/checkout';

import { FaiService } from '@services/fai.services';
import { HandleErrorService } from '@services/handle-error.service';
import { MainCartService } from '@services/main-cart.service';
import { MobileRecoveryService } from '@services/mobile-recovery.service';

import { BasicObject } from '@interfaces/checkout.interface';

import { CookieService } from 'ngx-cookie-service';
import { mergeMap } from 'rxjs/operators';

import { DataCyDirective } from '../../common/directives/data-cy.directive';
import { CorbisCreateCartBodyPY } from '../../interfaces/api/corbis.interface';
import { ProductsConfig } from '../../interfaces/mock.interface';
import { PaymentService } from '../../services/payment.service';
import { PHONES_MOCK } from '../mocks/configurator/phone.mock';
import { PY_MOCK } from '../mocks/eligibilite.mock';
import { FAI_MOCK } from './../mocks/configurator/fai.mock';
import { PLANS_MOCK } from './../mocks/configurator/plan.mock';
import { ACCESSORIES_MOCK, OPTIONS_MOCK } from './../mocks/configurator/xsell.mock';

export interface Configurator {
    file: string;
    json?: string;
    key: string;
    label: string;
}

export interface ProductConfig {
    [index: string]: ProductsConfig;
}

/** @dynamic */
@Component({
    selector: 'checkout-mockups-products',
    templateUrl: './mockups-products.component.html',
    styleUrls: ['./mockups-products.scss'],
    standalone: true,
    imports: [ReactiveFormsModule, NgClass, DataCyDirective],
})
export class MockupsProductsComponent implements OnInit {
    public formGroup: FormGroup<{
        config: FormControl<string | null>;
    }>;
    public isRenewal: boolean = false;
    public hasEdp: boolean = false;

    public phones = PHONES_MOCK;
    public fai = FAI_MOCK;
    public plans = PLANS_MOCK;
    public accessories = ACCESSORIES_MOCK;
    public options = OPTIONS_MOCK;

    public quotes: Configurator[] = [
        {
            label: 'Aucun',
            key: '',
            file: '',
        },
        {
            label: 'Panier SOWO',
            key: 'sowo',
            file: 'panier-sowo.json',
        },
        {
            label: 'Panier SOWO + Tel',
            key: 'sowo-phone',
            file: 'panier-sowo-phone.json',
        },
        {
            label: 'Panier SAS',
            key: 'sas',
            file: 'panier-sas.json',
        },
        {
            label: 'Panier SAS + Tel',
            key: 'sas-phone',
            file: 'panier-sas-phone.json',
        },
        {
            label: 'Panier SIMO',
            key: 'simo',
            file: 'panier-simo.json',
        },
        {
            label: 'Panier SIMO + Tel',
            key: 'simo-phone',
            file: 'panier-simo-phone.json',
        },
        {
            label: 'Panier FAIM',
            key: 'faim',
            file: 'panier-faim.json',
        },
        {
            label: 'Panier FAIM + Box',
            key: 'faim-box',
            file: 'panier-faim-box.json',
        },
        {
            label: 'Panier FAIM Premium',
            key: 'faim-premium',
            file: 'panier-faim-premium.json',
        },
        {
            label: 'Panier FAIM Premium + Box',
            key: 'faim-premium-box',
            file: 'panier-faim-premium-box.json',
        },
        {
            label: 'Panier accessoires nus',
            key: 'accessoires',
            file: 'panier-accessoires.json',
        },
    ];
    public type: string;

    protected paymentService: PaymentService = inject(PaymentService);

    public constructor(
        // NOSONAR
        protected http: HttpClient,
        protected router: Router,
        protected form: FormBuilder,
        protected cartService: MainCartService,
        protected handleErrorService: HandleErrorService,
        protected recoveryService: MobileRecoveryService,
        protected cookieService: CookieService,
        protected edpService: EdpService,
        protected addProductService: AddFundingService,
        protected faiService: FaiService,
        @Inject(DOCUMENT) private document: Document,
    ) {}

    public async addProduct(product: ProductsConfig, isFai: boolean = false): Promise<void> {
        const gencode: string = product?.gencode;
        const windowObjDom: Window = this._getWindow();

        if (isFai) {
            try {
                const corbisCartId: string = await this.faiService.createCorbisCart(CorbisCreateCartBodyPY);
                const mock = PY_MOCK;
                mock.data.idPanier = corbisCartId;
                windowObjDom.localStorage.setItem('provider_results', JSON.stringify(PY_MOCK));
                windowObjDom.localStorage.setItem('fai_elige_tokyo', '{"eligibiliteTokyo":false}');
                this.addFaiProducts(gencode);
            } catch (error) {
                console.log('Error adding FAI prodcut', error);
            }
        } else {
            this.addProducts([product]);
        }
    }

    public addFaiProducts(gencode: string): void {
        this.faiService.save();
        this.faiService.addFai(gencode).subscribe();
    }

    public addProducts(productsList: ProductsConfig[]): void {
        const quoteIndex = this.cartService.getQuoteIndexByContext(QUOTE_CONTEXTS.ACQUISITION);
        if (quoteIndex !== -1) {
            const gencodes: string[] = productsList.map((p) => p.gencode);
            this.cartService.addProducts(gencodes, false).subscribe({
                next: (cartModel: CheckoutCartModel) => {
                    console.log('Your cart has been updated: ', cartModel);
                },
                error: (error: Error) => this.handleErrorService.handleError(error),
            });
        }
    }

    public addRecovery(): void {
        this._getWindow().sessionStorage.setItem('mobileRecoveryCurrentStep', '2');
        // eslint-disable-next-line max-len
        this._getWindow().sessionStorage.setItem('recoveryDevisRepriseMobileOut-10148840', '');
        this._getWindow().sessionStorage.setItem(
            'mobile_recovery_current_product',
            // eslint-disable-next-line max-len
            '{"product":{"bonus":30,"dates":{},"hasBonus":true,"item":{"name":"P30 Lite","url":"https://www.bouyguestelecom.fr/telephones-mobiles/huawei-p30-lite-128-go"}},"status":"completed","step":"payment","recovery":{"product":{"name":"iPhone 8 64GB","brand":"Apple"},"criterias":{"items":[{"libelle":"Le téléphone présente-t\'il un aspect extérieur abîmé ?","id":"housing","reponses":[{"libelle":"Non","id":"88"},{"libelle":"Oui","id":"87"}],"tooltip":"Lorem ipsum dolor sit amet"},{"libelle":"L\'appareil s\'allume t-il correctement ?","id":"working","reponses":[{"libelle":"Oui","id":"74"},{"libelle":"Non","id":"75"}],"tooltip":"Lorem ipsum dolor sit amet"},{"libelle":"Les boutons et l\'écran sont-ils fonctionnels ?","id":"screen","reponses":[{"libelle":"Oui","id":"76"},{"libelle":"Non","id":"77"}],"tooltip":"Lorem ipsum dolor sit amet"},{"libelle":"La fonction \'Localiser mon iPhone\' a bien été désactivée ?","id":"ios_lock","reponses":[{"libelle":"Oui","id":"78"},{"libelle":"Non","id":"79"}],"tooltip":"Lorem ipsum dolor sit amet"}],"id":"10148840"},"estimation":{"amount":140,"items":{"id":"10148840","criteresReprise":[{"id":"housing","question":"Le téléphone présente-t\'il un aspect extérieur abîmé ?","reponse":{"id":"88","value":"Non"}},{"id":"working","question":"L\'appareil s\'allume t-il correctement ?","reponse":{"id":"74","value":"Oui"}},{"id":"screen","question":"Les boutons et l\'écran sont-ils fonctionnels ?","reponse":{"id":"76","value":"Oui"}},{"id":"ios_lock","question":"La fonction \'Localiser mon iPhone\' a bien été désactivée ?","reponse":{"id":"78","value":"Oui"}}]},"shipping":[{"modeLivraison":"ENVELOPPE_PRE_AFFRANCHIE","montantAssurance":-5,"montantLivraison":0,"insurance":true}],"id":"105886679"}},"recovery_id":"580501", "iban":"FR7630004001522017140117961"}',
        );
        this.recoveryService.init();
    }

    public toggleRenew(): void {
        const currentQuote: QuoteModel = this.cartService.getQuote();
        if (this.isRenewal) {
            this.cartService.addQuoteWithContext(QuoteContextAcquisitionModel).subscribe((quoteIndex) => {
                const quote = this.cartService.getQuote(quoteIndex);
                currentQuote.context = quote.context;
                this.cookieService.delete('oauth2_contract_id');
                this._refreshRenew();
            });
        } else {
            const params: BasicObject = {
                contractId: 10080092064,
            };

            this.cartService
                .addQuoteWithContext(QuoteContextRenewalModel, params)
                .pipe(
                    mergeMap(() => {
                        const gencodes: string[] = ['6762', '0194253402169'];
                        return this.cartService.addProductsByGencodes(gencodes);
                    }),
                )
                .subscribe(() => this._refreshRenew());
        }
    }

    public setFundingCash(): void {
        this._setFundingDefaultValue(FUNDING_ENUM.CASH);
    }

    public setFundingEdp(): void {
        this._setFundingDefaultValue(FUNDING_ENUM.EDP);
    }

    public setFundingYounited(): void {
        this._setFundingDefaultValue(FUNDING_ENUM.YOUNITED_X12);
    }

    public setFundingSwappy(): void {
        this._setFundingDefaultValue(FUNDING_ENUM.SWAPPY_X36);
    }

    public getScoring(): void {
        this.paymentService.scoring().subscribe();
    }

    public setPromoBanner(): void {
        const bannerContainer = this.document.getElementById('promoBanner');
        const banner = `
        <div class="notification is-small has-body is-banner has-text-centered" style="box-shadow: none;padding: 1.4rem;">
        <div class="body has-text-centered">
        <div class="sticker is-small is-primary" style="text-transform: capitalize; margin-right:10px;">Bon plan</div>
        <strong class="text is-level-3"> test, <span class="has-text-secondary has-text-weight-bold">-130€ de remise</span> sur un mobile avec votre forfait actuel avec le code test. <span onclick="copyCode()" id="copyLink" class="link" data-tc-clic-imp="AnimationCommerciale_EC_renouv_fam_IS_bandeauMDT_abandonnisteengage_cpu">Copier le code</span></strong>
        </div>
        </div>
       `;
        bannerContainer.innerHTML = banner;
        const bannerScript = document.createElement('script');
        const script = document.createTextNode(`
            function copyCode() {
                const codeToCopy = "test";
                navigator.clipboard.writeText(codeToCopy);
                document.getElementById("copyLink").innerText = "code copié";
            }`);
        bannerScript.appendChild(script);
        bannerContainer.appendChild(bannerScript);
    }

    public setIsPro(): void {
        this.cartService.setIsFromProWall(true);
        this.cartService.refresh();
    }

    public ngOnInit(): void {
        const currentConfigKey = this.cartService.cartModel.configId || '';

        this.isRenewal = this.cartService.getQuote().isRenewal();

        this.hasEdp = !!this.cartService.getQuote().getProductByType('Edp');

        this.formGroup = this.form.group({
            config: currentConfigKey,
        });
    }

    public addConvergence(): void {
        sessionStorage.setItem('promoConfigurator', '{"portability":true,"convergence":true}');
    }

    public addPack(url: string): void {
        const urlKeys = url.replace('pack-', '').split('_');
        this.cartService.addProductsByAttributes(urlKeys, 'url_key').subscribe();
    }

    private _getWindow(): Window {
        return this.document.defaultView.window;
    }

    private _refreshRenew(): void {
        const currentQuote: QuoteModel = this.cartService.getQuote();
        this.isRenewal = currentQuote.isRenewal();
    }

    private _setFundingDefaultValue(value: FUNDING_ENUM): void {
        this.addProductService.setFundingDefaultValue(this.cartService.cartModel, value).subscribe();
    }
}
